import type { ReactNode } from 'react';
import { useLingui } from '@lingui/react/macro';

import type { ActionMenuItem } from '@rover/components/src/callouts/ActionMenu';
import ActionMenu from '@rover/components/src/callouts/ActionMenu';
import PlayGlyph from '@rover/icons/svg/play-glyph.svg';
import { Box, Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { withOpacityRgba } from '@rover/kibble/utils';

import {
  ACTIONS_TESTID,
  DIMMED_IMAGE_OPACITY,
  GALLERY_GRID_COLUMNS,
  PLAY_BADGE_OPACITY,
  PLAY_BADGE_SIZE,
} from './constants';
import GallerySelectionIndicator from './GallerySelectionIndicator';
import type { GalleryItem } from './ImageGallery.common';
import { galleryItemKey, isGalleryVideoItem } from './ImageGallery.common';
import ImageWithShimmer from './ImageWithShimmer';

type GalleryTileProps = {
  image: GalleryItem;
  index: number;
  onImageClick: (index: number) => void;
  overlay?: ReactNode;
  actions?: ActionMenuItem[];
  onActionsOpenChange?: (isOpen: boolean) => void;
  isDimmed?: boolean;
  isManaging?: boolean;
  isSelectable?: boolean;
  isSelected?: boolean;
  onToggleSelected?: () => void;
  /**
   * Tiles per row, which decides which tiles are in the first row and so load
   * eagerly. The grid itself is `auto-fill`, so its real column count is only
   * known once it has been laid out; callers that know it should pass it.
   */
  columns?: number;
};

const PLAY_BADGE_SIZE_PX = `${PLAY_BADGE_SIZE}px`;

const GalleryTile = ({
  image,
  index,
  onImageClick,
  overlay,
  actions,
  onActionsOpenChange,
  isDimmed = false,
  isManaging = false,
  isSelectable = false,
  isSelected = false,
  onToggleSelected,
  columns = GALLERY_GRID_COLUMNS,
}: GalleryTileProps): JSX.Element => {
  const { t } = useLingui();
  const theme = useTheme();
  const playBadgeBackground = withOpacityRgba(theme.textColor.primary, PLAY_BADGE_OPACITY);
  const playBadgeFill = theme.textColor.primary_inverse;
  const isVideo = isGalleryVideoItem(image);
  const photoNumber = index + 1;
  // The number only stands in for a photo with no description.
  const photoName =
    (isVideo ? image.video.description : image.altText || image.caption) || t`photo ${photoNumber}`;
  const overlayId = `gallery-image-overlay-${galleryItemKey(image)}`;
  // The first row is above the fold, so those photos are worth fetching
  // eagerly; everything below stays lazy.
  const isAboveTheFold = index < columns;

  // Opening the lightbox would drop the mode the viewer is using.
  let onTilePress: (() => void) | undefined;
  if (isSelectable) {
    onTilePress = onToggleSelected;
  } else if (!isManaging) {
    onTilePress = () => onImageClick(index);
  }

  // The tile opens the lightbox, so its name has to say so even when the photo
  // carries a caption of its own. A tile that presses nowhere keeps its plain
  // name instead, rather than offering an action it does not have.
  const ownCaption = isVideo ? undefined : image.caption;
  let captionLabel: string;
  if (isVideo) {
    captionLabel = image.video.description;
  } else if (!onTilePress) {
    captionLabel = photoName;
  } else if (ownCaption) {
    captionLabel = t`View photo ${photoNumber}: ${ownCaption}`;
  } else {
    captionLabel = t`View photo ${photoNumber}`;
  }

  return (
    <>
      <Box sx={isDimmed ? { opacity: DIMMED_IMAGE_OPACITY } : undefined}>
        <ImageWithShimmer
          fluid
          src={image.small}
          caption={captionLabel}
          altText={isVideo ? undefined : image.altText}
          pressLabel={isSelectable ? t`Select ${photoName}` : undefined}
          selected={isSelectable ? isSelected : undefined}
          onClick={onTilePress}
          priority={isAboveTheFold}
          describedById={overlay ? overlayId : undefined}
        />
      </Box>
      {/* The grid shows the video's thumbnail with a play badge, never a
          player. Opening the tile is what starts playback, in the lightbox. */}
      {isVideo && (
        <Flex
          position="absolute"
          top="0"
          width="100%"
          height="100%"
          justifyContent="center"
          alignItems="center"
          data-testid="gallery-play-icon"
          // The badge sits over the tile's own click target, so it has to
          // let presses through rather than swallow them.
          sx={{ pointerEvents: 'none' }}
        >
          <Flex
            width={PLAY_BADGE_SIZE_PX}
            height={PLAY_BADGE_SIZE_PX}
            borderRadius="round"
            justifyContent="center"
            alignItems="center"
            sx={{ backgroundColor: playBadgeBackground }}
          >
            <PlayGlyph
              height={PLAY_BADGE_SIZE_PX}
              width={PLAY_BADGE_SIZE_PX}
              fill={playBadgeFill}
            />
          </Flex>
        </Flex>
      )}
      {!!actions?.length && (
        <Box position="absolute" top="2x" right="2x" zIndex="1" data-testid={ACTIONS_TESTID}>
          <ActionMenu
            items={actions}
            triggerLabel={t`Photo ${photoNumber} actions`}
            onOpenChange={onActionsOpenChange}
          />
        </Box>
      )}
      {overlay && (
        <Flex
          id={overlayId}
          position="absolute"
          bottom="0"
          left="0"
          maxWidth="100%"
          padding="2x"
          data-testid="gallery-image-overlay"
          sx={{ pointerEvents: 'none' }}
        >
          {overlay}
        </Flex>
      )}
      {isSelectable && (
        <Flex position="absolute" bottom="0" right="0" padding="2x" sx={{ pointerEvents: 'none' }}>
          <GallerySelectionIndicator checked={isSelected} />
        </Flex>
      )}
    </>
  );
};

export default GalleryTile;
